import { CommonModule } from "@angular/common";
import { Component, computed, inject, signal } from "@angular/core";
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
  RouterOutlet
} from "@angular/router";
import { Meta, Title } from "@angular/platform-browser";
import { filter } from "rxjs";
import { AuthService } from "./services/auth.service";
import { ThemeService } from "./services/theme.service";

const SEO_BASE_URL = "https://javastreams.mimiotech.com";
const DEFAULT_TITLE = "javaStreams | Java Streams Practice";
const DEFAULT_DESCRIPTION =
  "Practice Java Streams with curated Stream API challenges, editorials, discussions, and a Monaco-based code workspace.";

@Component({
  selector: "app-root",
  standalone: true,
  imports: [CommonModule, RouterOutlet, RouterLink, RouterLinkActive],
  templateUrl: "./app.component.html",
  styleUrl: "./app.component.css"
})
export class AppComponent {
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly titleService = inject(Title);
  private readonly meta = inject(Meta);
  private readonly themeService = inject(ThemeService);
  private readonly currentUrl = signal(this.router.url);
  readonly user = this.auth.user;
  readonly theme = this.themeService.theme;
  readonly showShellNav = computed(() => {
    const current = this.currentUrl();
    return this.auth.isLoggedIn() && !["/auth"].includes(current);
  });

  constructor() {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event) => {
      const url = (event as NavigationEnd).urlAfterRedirects;
      this.currentUrl.set(url);
      this.updateSeo(url);
    });

    this.updateSeo(this.router.url);
  }

  logout() {
    this.auth.logout();
  }

  toggleTheme() {
    this.themeService.toggleTheme();
  }

  private updateSeo(url: string) {
    const activeRoute = this.getDeepestChild(this.route);
    const data = activeRoute.snapshot.data as { title?: string; description?: string } | undefined;
    const title = data?.title ?? DEFAULT_TITLE;
    const description = data?.description ?? DEFAULT_DESCRIPTION;
    const canonicalUrl = this.buildCanonicalUrl(url);

    this.titleService.setTitle(title);
    this.meta.updateTag({ name: "description", content: description });
    this.meta.updateTag({ property: "og:title", content: title });
    this.meta.updateTag({ property: "og:description", content: description });
    this.meta.updateTag({ property: "og:url", content: canonicalUrl });
    this.meta.updateTag({ name: "twitter:title", content: title });
    this.meta.updateTag({ name: "twitter:description", content: description });
    this.meta.updateTag({ name: "twitter:url", content: canonicalUrl });

    this.setCanonicalLink(canonicalUrl);
  }

  private getDeepestChild(route: ActivatedRoute): ActivatedRoute {
    let active = route;
    while (active.firstChild) {
      active = active.firstChild;
    }
    return active;
  }

  private buildCanonicalUrl(url: string) {
    const cleanBase = SEO_BASE_URL.replace(/\/+$/, "");
    const path = url.split("#")[0];
    return `${cleanBase}${path.startsWith("/") ? path : `/${path}`}`;
  }

  private setCanonicalLink(url: string) {
    const existing = document.querySelector("link[rel='canonical']") as HTMLLinkElement | null;
    if (existing) {
      existing.href = url;
      return;
    }

    const link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    link.setAttribute("href", url);
    document.head.appendChild(link);
  }
}
